import type { ChangeEvent } from "react";

import { formatAudioTime } from "../../utils/format-audio-time";

type AudioPlayerProgressProps = {
  disabled?: boolean;
  durationSeconds: number;
  onSeek: (event: ChangeEvent<HTMLInputElement>) => void;
  progressMax: number;
  progressValue: number;
};

/**
 * The unfilled portion comes from the `<progress>` background while the filled
 * portion is the browser's own progress value, so no width has to be computed
 * in JS. The range input that sits on top stays transparent and owns keyboard
 * and assistive-technology interaction.
 */
const PROGRESS_TRACK_CLASS_NAME =
  "h-1.5 w-full appearance-none overflow-hidden rounded-full bg-new-beginnings-tints-900 [&::-moz-progress-bar]:bg-new-beginnings-base [&::-webkit-progress-bar]:bg-transparent [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-new-beginnings-base";

/**
 * The thumb is transparent at rest to match the design's unbroken bar, and
 * appears on hover or keyboard focus so the drag affordance stays discoverable.
 */
const PROGRESS_INPUT_CLASS_NAME =
  "absolute inset-0 size-full cursor-pointer appearance-none bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-new-beginnings-base focus-visible:ring-offset-2 disabled:cursor-not-allowed [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-new-beginnings-base [&::-moz-range-thumb]:opacity-0 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-new-beginnings-base [&::-webkit-slider-thumb]:opacity-0 [&:focus-visible::-moz-range-thumb]:opacity-100 [&:focus-visible::-webkit-slider-thumb]:opacity-100 [&:hover::-moz-range-thumb]:opacity-100 [&:hover::-webkit-slider-thumb]:opacity-100";

function AudioPlayerProgress({
  disabled = false,
  durationSeconds,
  onSeek,
  progressMax,
  progressValue,
}: AudioPlayerProgressProps) {
  return (
    <div className="relative flex min-w-0 flex-1 items-center">
      <progress
        aria-hidden="true"
        className={PROGRESS_TRACK_CLASS_NAME}
        max={progressMax}
        value={progressValue}
      />
      <input
        aria-label="Audio progress"
        aria-valuetext={`${formatAudioTime(progressValue)} of ${formatAudioTime(durationSeconds)}`}
        className={PROGRESS_INPUT_CLASS_NAME}
        disabled={disabled}
        max={progressMax}
        min="0"
        onChange={onSeek}
        type="range"
        value={progressValue}
      />
    </div>
  );
}

export { AudioPlayerProgress };
